import { IconGitHub } from "@/assets/icons/geist/IconGitHub";
import { IconGoogle } from "@/assets/icons/geist/IconGoogle";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { IconSandglass } from "@/assets/icons/geist/IconSandglass";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconKey2 } from "central-icons/IconKey2";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import { Alert } from "@/components/podkit/alert/Alert";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import { TrackLocations } from "@/hooks/use-segment";
import { useGetDomainVerification, useUpdateSSOConfiguration, useVerifyDomain } from "@/queries/organization-queries";
import { SetupSSOModal, type SetupSSOModalStep } from "@/routes/settings/login/SetupSSOModal";
import { formatError } from "@/utils/errors";
import { Switch } from "@/components/Switch";
import {
    DomainVerificationState,
    SSOConfigurationState,
    UpdateSSOConfigurationRequest,
    type SSOConfiguration,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useState, type FC } from "react";

type SSOConfigCardProps = {
    organizationId: string;
    config?: SSOConfiguration;
    deactivateable: boolean;
    editable?: boolean;
};

export const CustomSSOConfigCard: FC<SSOConfigCardProps> = ({
    organizationId,
    config,
    deactivateable,
    editable = true,
}) => {
    const { toast } = useToast();
    const [showSetupSSO, setShowSetupSSO] = useState<SetupSSOModalStep | undefined>(undefined);

    const { domainVerification } = useGetDomainVerification(config?.emailDomain);
    const domain = domainVerification?.domain || "";
    const isDomainVerified = domainVerification?.state === DomainVerificationState.VERIFIED;

    const verifyDomain = useVerifyDomain();
    const handleVerifyDomain = useCallback(async () => {
        if (!domainVerification) {
            return;
        }
        try {
            const result = await verifyDomain.mutateAsync(domainVerification);
            const title =
                result?.state === DomainVerificationState.VERIFIED
                    ? `${domain} is verified.`
                    : `${domain} is not yet verified. Please try again later.`;

            toast({
                title,
            });
        } catch (error) {
            toast({
                title: "Failed to verify domain.",
                description: formatError(error),
            });
        }
    }, [domain, domainVerification, toast, verifyDomain]);

    const description = domain && (
        <span className="flex flex-row items-center gap-1 pt-1 text-base text-content-primary">
            {isDomainVerified ? (
                <>
                    <IconCircleCheck size={20} className="text-content-green" aria-hidden /> {domain}{" "}
                    <span className="sr-only">(verified)</span>
                </>
            ) : (
                <Tooltip content="The Login with SSO will be available after the domain is verified.">
                    <span className="flex flex-row items-center gap-1">
                        <IconSandglass /> {domain} pending
                    </span>
                </Tooltip>
            )}
        </span>
    );

    const banner = domain && !isDomainVerified && (
        <Alert className="flex flex-row items-center gap-4 border-0 bg-surface-02 p-3">
            <Text className="text-base text-content-secondary">
                Waiting to verify your domain. Click to retry the verification.
            </Text>
            <div className="grow" />
            <Button
                disabled={!domainVerification}
                onClick={handleVerifyDomain}
                loading={verifyDomain.isPending}
                variant={"secondary"}
                size={"md"}
                LeadingIcon={IconRefresh}
                data-tracking-id="verify-domain-alert-custom-sso-config-card"
            />
        </Alert>
    );

    const title = "Single Sign-On";

    const toggle = config && isDomainVerified && (
        <EnablementToggle title={title} config={config} deactivateable={deactivateable} editable={editable} />
    );

    const actions = (
        <>
            {!config && (
                <Button
                    disabled={!editable}
                    variant={"primary"}
                    size={"sm"}
                    onClick={() => setShowSetupSSO("configure-sso")}
                    data-tracking-id="set-up-custom-sso-config-card"
                >
                    Set Up
                </Button>
            )}
            {config && !isDomainVerified && (
                <>
                    <Button
                        disabled={!editable}
                        variant={"secondary"}
                        size={"sm"}
                        onClick={() => setShowSetupSSO("configure-sso")}
                        data-tracking-id="edit-sso-unverified-custom-sso-config-card"
                    >
                        Edit
                    </Button>
                    <Button
                        disabled={!editable}
                        variant={"secondary"}
                        size={"sm"}
                        onClick={() => setShowSetupSSO("domain-verification")}
                        data-tracking-id="verify-domain-custom-sso-config-card"
                    >
                        Verify
                    </Button>
                </>
            )}
            {config && isDomainVerified && (
                <Button
                    disabled={!editable}
                    variant={"secondary"}
                    size={"sm"}
                    onClick={() => setShowSetupSSO("configure-sso")}
                    data-tracking-id="edit-sso-verified-custom-sso-config-card"
                >
                    Edit
                </Button>
            )}
        </>
    );

    return (
        <>
            <Card className="flex w-[550px] flex-col gap-4 bg-surface-glass p-4" variant="bordered">
                <div className="flex items-center gap-2">
                    {toggle}
                    <IconKey2 size={24} className="text-content-primary" />
                    <p className="text-base font-normal text-content-primary">{title}</p>
                    {!description && actions && <div className="ml-auto flex items-center gap-4">{actions}</div>}
                </div>

                {description && (
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">{description}</div>
                        {actions && <div className="ml-auto flex items-center gap-4">{actions}</div>}
                    </div>
                )}

                {banner}
            </Card>

            {showSetupSSO && (
                <SetupSSOModal
                    organizationId={organizationId}
                    config={config}
                    step={showSetupSSO}
                    onClose={() => {
                        setShowSetupSSO(undefined);
                    }}
                />
            )}
        </>
    );
};

export const BuiltinSSOConfigCard: FC<SSOConfigCardProps> = ({ config, deactivateable, editable = true }) => {
    const title = config ? getTitle(config) : "";

    const icon = config ? getIcon(config) : undefined;
    const toggle = config && (
        <EnablementToggle
            title={title || getTitle(config)}
            config={config}
            deactivateable={deactivateable}
            editable={editable}
        />
    );

    return (
        <Card className="flex w-[550px] flex-col gap-4 bg-surface-glass p-4" variant="bordered">
            <div className="flex items-center gap-2">
                {toggle}
                {icon ?? <IconKey2 size={24} className="text-content-primary" />}
                <p className="text-base font-normal text-content-primary">{title}</p>
            </div>
        </Card>
    );
};

const getTitle = (config: SSOConfiguration) => {
    if (config.issuerUrl?.includes("github")) {
        return "GitHub";
    }
    if (config.issuerUrl?.includes("google")) {
        return "Google";
    }
    return "Unknown";
};

const getIcon = (config: SSOConfiguration) => {
    if (config.issuerUrl?.includes("github")) {
        return <IconGitHub size="lg" className="size-6" />;
    }
    if (config.issuerUrl?.includes("google")) {
        return <IconGoogle size="lg" className="size-6" />;
    }
    return undefined;
};

const EnablementToggle: FC<{
    config: SSOConfiguration;
    title: string;
    deactivateable: boolean;
    editable: boolean;
}> = ({ config: { id, state }, title, deactivateable, editable }) => {
    const updateSSOConfig = useUpdateSSOConfiguration();
    const { toast } = useToast();

    const [showModal, setShowModal] = useState(false);

    const isActive = state === SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE;

    const updateState = useCallback(
        async (state: SSOConfigurationState) => {
            try {
                await updateSSOConfig.mutateAsync(
                    new UpdateSSOConfigurationRequest({
                        ssoConfigurationId: id,
                        state,
                    }),
                );
            } catch (error) {
                toast({
                    title: "Failed to update SSO configuration",
                    description: formatError(error),
                });
            }
        },
        [id, toast, updateSSOConfig],
    );

    const handleCheckedChange = useCallback(async () => {
        if (!isActive) {
            await updateState(SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE);
        } else {
            setShowModal(true);
        }
    }, [isActive, updateState]);

    const handleSubmitDeactivation = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            await updateState(SSOConfigurationState.SSO_CONFIGURATION_STATE_INACTIVE);
            setShowModal(false);
        },
        [updateState],
    );

    return (
        <>
            <Tooltip content={!deactivateable && isActive ? "You can't deactivate the single remaining provider." : ""}>
                <div className="flex items-center">
                    <Switch
                        state={isActive ? "checked" : "unchecked"}
                        disabled={(!deactivateable && isActive) || !editable}
                        onToggle={handleCheckedChange}
                        id={"toggle-" + id}
                    />
                </div>
            </Tooltip>
            {showModal && (
                <Dialog open onOpenChange={setShowModal}>
                    <DialogContent
                        className="max-w-[600px]"
                        data-testid="deactivate-form-modal-content"
                        data-track-location={TrackLocations.DeactivateSSOModal}
                    >
                        <DialogHeader>
                            <DialogTitle>Deactivating {title} log in </DialogTitle>
                            <DialogDescription />
                        </DialogHeader>

                        <DialogBody className="overflow-x max-w-full">
                            <form
                                id="deactivate-form"
                                onSubmit={handleSubmitDeactivation}
                                className="flex flex-col gap-4"
                            >
                                <Text className="text-base">Users will no longer be able to log in with {title}.</Text>
                                <Text className="text-base">
                                    They will need to create a new account using one of the other available log in
                                    methods.
                                </Text>
                            </form>
                        </DialogBody>

                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="button" variant="secondary" autoFocus={true}>
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button
                                type="submit"
                                variant="destructive"
                                form="deactivate-form"
                                loading={updateSSOConfig.isPending}
                                disabled={updateSSOConfig.isPending}
                                data-testid="deactivate-submit-button"
                                data-tracking-id="deactivate-sso-config-toggle"
                            >
                                Deactivate
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};
